import { Formik } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BsArrowLeftShort } from 'react-icons/bs';
import { Button, Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { decode } from '../../../../../helpers/utils';
import { setCurrentFloor, setEditingPinId } from '../../../../../store/slices/projectItemSlice';
import { useActiveTab } from '../../../../../components/map/components/hooks/useActiveTab';
import AutosaveForm from '../../../components/AutoSaveForm';
import useFlyToPin from '../../../../../components/map/components/hooks/useFlyToPin';

import { useSafetySubmit } from './hooks/useSafetyActions';
import { FormInitializer, useDebouncedAutoSave } from '../../utils/pinServices';
import { fetchSafetyById, fetchSafetyIcons, normalizeSafetyData } from './services/safetyService';
import SafetyFormFields from './components/SafetyFormFields';
import { Loader } from '../../utils/commonComponent';

const EditSafety = () => {
    useActiveTab('all');

    const dispatch     = useDispatch();
    const navigate     = useNavigate();
    const { subid }    = useParams();
    const decodedSubid = decode(subid);
    const flyToPin     = useFlyToPin();

    const projectData = useSelector((state) => state.api.projectData);
    const currentFloor = useSelector((state) => state.api.currentFloor);
    const floorList    = useSelector((state) => state.api.floorList);

    const [currentPinData, setCurrentPinData] = useState({});
    const [isDirty, setIsDirty]               = useState(false);
    const [color, setColor]                   = useState(null);
    const [openPicker, setOpenPicker]         = useState(null);
    const [planDetails, setPlanDetails]       = useState(null);
    const [planModal, setPlanModal]           = useState(false);
    const [safetyIcons, setSafetyIcons]       = useState([]);
    const [isSaving, setIsSaving]             = useState(false);
    const [loading, setLoading]               = useState(false);

    const pendingNavigation = useRef(false);

    useEffect(() => {
        if (!decodedSubid) return;

        const load = async () => {
            setLoading(true)
            try {
                const data = await fetchSafetyById(decodedSubid);
                const { prefillData } = normalizeSafetyData(data);
                
                dispatch(setEditingPinId(decodedSubid));
                setCurrentPinData(prefillData);
                
                // if (data?.positions) {
                //     flyToPin(JSON.parse(data.positions));
                // }
                setLoading(false)
            } catch (err) {
                console.error('Failed to load product:', err);
                setLoading(false)
            }
        };

        load();
        fetchIcons(0);
    }, [decodedSubid]);

    useEffect(()=>{
        if(!currentPinData || !currentFloor || !floorList) return

        if(currentFloor?.enc_id !== currentPinData.enc_floor_plan_id){
            const found = floorList.find(
                (option) => String(option.enc_id) === String(currentPinData.enc_floor_plan_id)
            );
            if(found){
                dispatch(setCurrentFloor(found));
            }
        }
        if (currentPinData?.positions) {
            flyToPin(JSON.parse(currentPinData.positions));
        }

    },[currentPinData, currentFloor, floorList])

    const fetchIcons = async (id) => {
        let response = await fetchSafetyIcons(id);
        setSafetyIcons(response);
    };

    const handleAfterSave = useCallback(() => {
        setIsSaving(false);
        if (pendingNavigation.current) {
            pendingNavigation.current = false;
            navigate(-1);
        }
    }, [navigate]);

    const { submit } = useSafetySubmit({
        setCurrentPinData,
        setIsDirty,
        setPlanModal,
        setPlanDetails,
        onAfterSave: handleAfterSave,
    });

    const handleAutoSave = useCallback(() => {
        document.getElementById('safetySubmitBtn')?.click();
    }, []);

    const debouncedAutoSave = useDebouncedAutoSave(() => { document.getElementById('safetySubmitBtn')?.click(); }, 800);

    const goBack = () => {
        if (isDirty) {
            setIsSaving(true);
            pendingNavigation.current = true;
            document.getElementById('safetySubmitBtn')?.click();
        } else {
            navigate(-1);
        }
    };

    const initialValues = {
        safety_name: '! New safety',
        ...currentPinData,
    };

    return (
        <div
            className="bar"
            id="inner-customizer2"
            style={{ position: 'relative', height: window.innerHeight - 80, paddingBottom: 20 }}
        >
            {(isSaving || loading) && (
                <Loader/> 
            )}

            <Row className="backRow">
                <Col md={8}>
                    <h1>Safety Pin Details</h1>
                </Col>
                <Col md={4}>
                    <div className="backArrow float-right" onClick={goBack}>
                        <BsArrowLeftShort />
                    </div>
                </Col>
            </Row>

            <Formik
                initialValues={initialValues}
                onSubmit={submit}
            >
                {({ errors, values, touched, handleSubmit, handleChange, setFieldValue, setFieldError }) => (
                    <>
                        <FormInitializer currentPinData={currentPinData} />

                        {currentPinData?.position && !currentPinData?.enc_id && (
                            <AutosaveForm handleSubmit={handleAutoSave} />
                        )}

                        <form
                            className="av-tooltip tooltip-label-bottom formGroups"
                            onSubmit={handleSubmit}
                        >
                            <div
                                className="custom-scrollbar customScroll"
                                style={{ height: window.innerHeight - 80 }}
                            >
                                <div className="bar-sub">
                                    <SafetyFormFields
                                        values={values}
                                        errors={errors}
                                        touched={touched}
                                        handleChange={handleChange}
                                        setFieldValue={setFieldValue}
                                        color={color}
                                        setColor={setColor}
                                        openPicker={openPicker}
                                        setOpenPicker={setOpenPicker}
                                        setSelSafetyDtls={setCurrentPinData}
                                        setIsDirty={setIsDirty}
                                        projectSettings={projectData}
                                        safetyIcons={safetyIcons}
                                        handleAutoSave={handleAutoSave}
                                        debouncedAutoSave={debouncedAutoSave}
                                    />
                                </div>
                            </div>

                            <Button
                                id="safetySubmitBtn"
                                className="btn-primary bar-btn"
                                type="submit"
                                hidden
                            >
                                Submit
                            </Button>
                        </form>
                    </>
                )}
            </Formik>
        </div>
    );
};

export default EditSafety;
